<div class="card border-0 shadow-sm pricing-settings-card">
  <div class="card-header bg-white">
    <h5 class="mb-1 fw-semibold">
      <i class="bi bi-tags-fill text-primary me-2"></i>
      Consultation Pricing
    </h5>

    <small class="text-muted">
      Add billable services and amounts that can be reused when creating invoices.
    </small>
  </div>

  <div class="card-body">
    @if (isLoading) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
        Loading consultation pricing...
      </div>
    }

    @if (!isLoading) {
      @if (errorMessage) {
        <div class="alert alert-danger d-flex align-items-center">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          <span>{{ errorMessage }}</span>
        </div>
      }

      @if (statusMessage) {
        <div class="alert alert-success d-flex align-items-center">
          <i class="bi bi-check-circle-fill me-2"></i>
          <span>{{ statusMessage }}</span>
        </div>
      }

      <div class="alert alert-info d-flex align-items-start pricing-note">
        <i class="bi bi-info-circle-fill me-2 mt-1"></i>
        <div>
          Use this list to define every service item that can later appear on an invoice, for
          example scan, follow-up, or consultation fees.
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="card pricing-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Configured Services</div>
              <div class="fs-4 fw-bold">{{ totalServices }}</div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card pricing-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Average Cost</div>
              <div class="fs-4 fw-bold">R{{ formatCurrency(averageCost) }}</div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card pricing-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Highest Cost</div>
              <div class="fs-4 fw-bold">R{{ formatCurrency(highestCost) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 bg-light mb-4 pricing-form-card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <div>
              <h6 class="mb-1 fw-semibold">Add New Service</h6>
              <small class="text-muted"
                >Enter the service name and the amount you want to charge.</small
              >
            </div>

            <span class="badge text-bg-primary align-self-center"> Ready for invoices </span>
          </div>

          <div class="row g-3 align-items-end">
            <div class="col-lg-7">
              <label class="form-label fw-semibold">Service Item</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="draftItem.serviceItem"
                placeholder="Scan"
              />
            </div>

            <div class="col-lg-3">
              <label class="form-label fw-semibold">Cost</label>
              <div class="input-group">
                <span class="input-group-text">R</span>
                <input
                  type="number"
                  class="form-control"
                  min="0"
                  step="0.01"
                  [(ngModel)]="draftItem.cost"
                  placeholder="500"
                />
              </div>
            </div>

            <div class="col-lg-2 d-grid">
              <button class="btn btn-primary" type="button" (click)="addPricingItem()">
                <i class="bi bi-plus-circle me-2"></i>
                Add Item
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h6 class="mb-1 fw-semibold">Configured Pricing Items</h6>
          <small class="text-muted"
            >These items can be used later when building invoice line items.</small
          >
        </div>

        <span class="badge text-bg-secondary">{{ pricingItems.length }} items</span>
      </div>

      @if (pricingItems.length === 0) {
        <div class="empty-state border rounded-3 p-4 text-center text-muted">
          <i class="bi bi-receipt-cutoff fs-1 d-block mb-2"></i>
          No pricing items have been added yet.
        </div>
      }

      @for (item of pricingItems; track trackPricingItem($index, item); let i = $index) {
        <div class="card mb-2 border pricing-item-card">
          <div class="card-body py-3">
            <div class="row align-items-center g-3">
              <div class="col-md-7">
                <div class="fw-semibold">{{ item.serviceItem }}</div>
                <small class="text-muted">Billable consultation service</small>
              </div>

              <div class="col-md-3 text-md-end">
                <div class="text-muted small">Cost</div>
                <div class="fs-5 fw-bold text-primary">R{{ formatCurrency(item.cost) }}</div>
              </div>

              <div class="col-md-2 text-md-end">
                <button
                  class="btn btn-outline-danger btn-sm"
                  type="button"
                  (click)="removePricingItem(i)"
                >
                  <i class="bi bi-trash me-1"></i>
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    }
  </div>

  @if (!isLoading) {
    <div
      class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <small class="text-muted">
        Keep the pricing catalogue current so invoice generation stays consistent.
      </small>

      <button
        class="btn btn-primary"
        type="button"
        [disabled]="isSaving"
        (click)="savePricingItems()"
      >
        <i class="bi bi-floppy-fill me-2"></i>
        {{ isSaving ? 'Saving...' : 'Save Pricing Catalogue' }}
      </button>
    </div>
  }
</div>
